import { LinearGradient } from "expo-linear-gradient";
import { Button, Center, Divider, HStack, Text, VStack } from "native-base";
import React from "react";
import { FlatList } from "react-native";
import { createShimmerPlaceholder } from "react-native-shimmer-placeholder";
import styled from "styled-components/native";

import Breadcrumb from "@/components/atoms/Breadcrumb";
import RoundedLogo from "@/components/atoms/RoundedLogo";
import Container from "@/components/organisms/Container";
import ProductSectionList from "@/components/organisms/ProductSectionList";
import ProductSectionListLoading from "@/components/organisms/ProductSectionList/ProductSectionListLoading";
import StoreHighlightList from "@/components/organisms/StoreHighlightList";
import WebHeader from "@/components/organisms/WebHeader";
import { useAppNavigation } from "@/hooks/application/navigation";
import { usePromotionProducts } from "@/hooks/domain/promotions";
import { useStoreHallWithProducts, useStoreHalls } from "@/hooks/domain/storeHall";
import { HomeNavigation } from "@/navigation/stacks/HomeStack";
import { formatToCurrency } from "@/utils/helpers";

import { StoreHomeScreenProps } from "./types";

const ShimmerPlaceholder = createShimmerPlaceholder(LinearGradient);

const StoreHomeWeb = ({
  store,
  storeHighlights,
  shipmentValue,
  isLoadingShipmentValue,
  isLoadingStore,
  isLoadingStoreHighlights,
  onOpenProfile,
  onSelectHighlight,
}: StoreHomeScreenProps) => {
  const homeNavigation = useAppNavigation<HomeNavigation>();

  const { data: hallList } = useStoreHalls(store?._id, {
    enabled: !!store?._id,
  });

  const { data: promotionProducts, isLoading: isLoadingProducts } = usePromotionProducts({
    storeId: store?._id,
    limit: 10,
    options: {
      enabled: !!store?._id,
    },
  });

  const {
    data: hallProductList,
    isLoading: isLoadingHallProducts,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useStoreHallWithProducts(store?._id, {
    enabled: !!store?._id,
  });

  const onLoadMoreHallProducts = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const onSelectHall = (hallId: string) => {
    homeNavigation.navigate("hallCategoryList", { hallId });
  };

  const onSelectProduct = (productId: string) => {
    homeNavigation.navigate("product", { productId });
  };

  return (
    <Container
      px="5%"
      Header={
        <WebHeader
          title={store?.nome_fantasia}
          searchInStoreId={store?._id}
          badgeTitle="Corredores"
          badgeList={hallList}
          onPressBack={() => homeNavigation.goBack()}
          onSelectBadge={(item) => onSelectHall(item?._id)}
        />
      }>
      <VStack pt="4">
        <VStack w="100%" padding="4" zIndex={1} bgColor="transparent">
          <Breadcrumb
            routes={[
              { title: "Home", routeName: "home" },
              { title: store?.nome_fantasia, routeName: "storeHome" },
            ]}
          />
          <Center my="4">
            <HStack alignItems="center">
              <RoundedLogo logoUrl={store?.foto_estabelecimento} size="120px" imageSize="xl" />
              <VStack mx="4" space="2">
                {isLoadingStore ? (
                  <LoadingStoreName />
                ) : (
                  <Text fontSize="3xl" color="text.600" bold>
                    {store?.nome_fantasia}
                  </Text>
                )}
                {isLoadingShipmentValue ? (
                  <LoadingShipment />
                ) : (
                  <>
                    {typeof shipmentValue === "number" ? (
                      <Text fontSize="lg" color="primary.600" bold>
                        Frete {shipmentValue === 0 ? "Grátis" : formatToCurrency(shipmentValue)}
                      </Text>
                    ) : (
                      <Text fontSize="lg" color="error.600">
                        Não atende sua região
                      </Text>
                    )}
                  </>
                )}
                {!!store?.valor_minimo_pedido && (
                  <Text fontSize="lg">
                    Pedido mínimo: {formatToCurrency(store?.valor_minimo_pedido)}
                  </Text>
                )}
                <Button w="60px" h="26px" variant="link" onPress={onOpenProfile}>
                  Ver perfil
                </Button>
              </VStack>
            </HStack>
          </Center>
          {storeHighlights?.length !== 0 && (
            <VStack mt="8" p="4" shadow="4" rounded="lg">
              <Text fontSize="xl" color="text.600" bold>
                Destaques
              </Text>
              <Divider my="4" />
              <StoreHighlightList
                data={storeHighlights}
                isLoading={isLoadingStoreHighlights}
                onSelect={onSelectHighlight}
              />
            </VStack>
          )}
          {promotionProducts?.length !== 0 && (
            <ProductSectionList
              title="Promoções"
              data={promotionProducts}
              isLoading={isLoadingProducts}
              onSelect={onSelectProduct}
              onPressViewMore={() =>
                homeNavigation.navigate("promotionProductList", { storeId: store?._id })
              }
            />
          )}
          {isLoadingHallProducts && <ProductSectionListLoading />}
          {!!hallProductList?.length && (
            <FlatList
              data={hallProductList}
              renderItem={({ item: hall }) => (
                <ProductSectionList
                  title={hall.nome}
                  data={hall.produtos}
                  isLoading={isLoadingHallProducts}
                  onSelect={onSelectProduct}
                  onPressViewMore={() => onSelectHall(hall._id)}
                />
              )}
            />
          )}
          {hasNextPage && (
            <Center my="4">
              <Button
                w="full"
                variant="outline"
                isLoading={isFetchingNextPage}
                onPress={() => onLoadMoreHallProducts()}>
                Ver mais corredores
              </Button>
            </Center>
          )}
        </VStack>
      </VStack>
    </Container>
  );
};

const LoadingStoreName = styled(ShimmerPlaceholder)`
  width: 100%;
  height: 24px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const LoadingShipment = styled(ShimmerPlaceholder)`
  width: 50%;
  height: 16px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

export default StoreHomeWeb;
